import React from 'react';
import Navigation from '@/components/shared/Navigation';
import Footer from '@/components/shared/Footer';

const Exchange1031Blueprint = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans animate-slide-in">
      <Navigation />

      {/* Header */}
      <section className="bg-white py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm text-gray-500 mb-3">
            Real Estate Strategy · PulseShift Research
          </p>

          <h1 className="text-4xl font-semibold text-[#0A2540] mb-4">
            Mastering the 1031 Exchange
          </h1>

          <p className="text-xl text-[#2E2E2E] max-w-3xl">
            How sophisticated investors defer capital gains, preserve equity,
            and compound wealth through strategic exchanges.
          </p>
        </div>
      </section>

      {/* Content + PDF */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row gap-12">

          {/* LEFT — TEXT */}
          <div className="lg:w-[58%] space-y-10 text-[#2E2E2E] leading-relaxed">

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              1. Introduction: The Tax Drag Problem
            </h2>

            <p>
              In real estate investing, taxation is one of the most underestimated
              destroyers of compounding. A profitable exit does not equate to
              wealth creation if a significant portion of the gain is lost to taxes.
            </p>

            <p>
              The <strong>1031 Exchange</strong> exists as a structural solution
              — allowing investors to defer taxes, preserve capital, and redeploy
              full equity into the next opportunity.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              2. What is a 1031 Exchange?
            </h2>

            <p>
              Under Section 1031 of the Internal Revenue Code, investors can sell
              an investment property and reinvest into another qualifying asset
              without triggering immediate capital gains tax.
            </p>

            <div className="font-mono bg-gray-50 p-4 rounded border">
              Sell → Reinvest → Defer Taxes
            </div>

            <blockquote className="border-l-4 border-[#4A6FA5] pl-4 italic text-gray-600">
              “This is not tax avoidance — it is disciplined tax deferral.”
            </blockquote>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              3. The Hidden Power: Equity Preservation
            </h2>

            <p>
              The real advantage is not saving tax — it is preserving capital.
              Investors reinvest 100% of equity instead of post-tax proceeds,
              creating exponential compounding over multiple cycles.
            </p>

            <p className="font-semibold">
              Small differences in capital today create massive divergence in
              long-term wealth.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              4. The Rules That Define the Game
            </h2>

            <ul className="list-disc pl-6 space-y-2">
              <li><strong>45 Days:</strong> Identify replacement property</li>
              <li><strong>180 Days:</strong> Complete acquisition</li>
              <li><strong>Qualified Intermediary:</strong> Mandatory</li>
              <li><strong>Like-Kind Requirement:</strong> Must qualify</li>
            </ul>

            <p>
              Failure to meet any requirement results in full tax liability.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              5. Strategy Layer: How Professionals Use It
            </h2>

            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Trade-Up:</strong> Smaller → institutional assets</li>
              <li><strong>Market Rotation:</strong> Exit hot, enter growth markets</li>
              <li><strong>Yield Optimization:</strong> Core → value-add</li>
              <li><strong>Portfolio Consolidation:</strong> Multiple → single asset</li>
            </ul>

            <p>
              The 1031 exchange is not just a tax tool — it is a portfolio
              optimization strategy.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              6. The Risk: When Strategy Breaks
            </h2>

            <ul className="list-disc pl-6 space-y-2">
              <li>Forced buying due to timelines</li>
              <li>Overpaying in competitive markets</li>
              <li>Execution complexity</li>
              <li>Capital lock-in</li>
            </ul>

            <blockquote className="border-l-4 border-[#4A6FA5] pl-4 italic text-gray-600">
              “A bad deal with tax deferral is still a bad deal.”
            </blockquote>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              7. Conclusion: Engineering Compounding
            </h2>

            <p>
              The 1031 Exchange enables investors to control capital flow,
              preserve equity, and scale portfolios over time.
            </p>

            <p className="font-semibold">
              The question is not whether you defer taxes — but whether you use
              that deferral to engineer long-term wealth.
            </p>

            <p className="text-sm text-gray-500 pt-6">
              Source: PulseShift Research | Real Estate Strategy
            </p>

          </div>

          {/* RIGHT — PDF */}
          <div className="lg:w-[42%] sticky top-24 self-start">
            <div className="h-[85vh] border rounded-lg overflow-hidden shadow-sm">
              <iframe
                src="/1031_Exchange_Blueprint.pdf#view=FitH&navpanes=0&scrollbar=0"
                title="1031 Exchange PDF"
                className="w-full h-full"
              />
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Exchange1031Blueprint;
